import React, { useEffect, useState } from "react";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";
import ListItemText from "@mui/material/ListItemText";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faChevronLeft,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import { Link, useNavigate } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import MenuItemComponent from "./MenuItemComponent";
import { useI18nField, useL, useLocale } from "../locale/LocaleContext";
import LangToggle from "./LangToggle";
import "@/components/CSS/MobileMenu.scss";

// Back link (now a button)
const BackLink = ({ getToRoot }) => {
  const t = useL();
  return (
    <button className="nav__link sub__close" onClick={getToRoot} type="button">
      <FontAwesomeIcon icon={faChevronLeft} /> {t({ en: "Back", fr: "Retour" })}
    </button>
  );
};

// Recursive menu item for subdepartments
const MenuItem = ({ label, link, subRoutes = [], toggleMenu, deptId }) => {
  const isExpandable = subRoutes.length > 0;
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleClick = (e) => {
    e.preventDefault();
    if (isExpandable) {
      setOpen((v) => !v);
    } else {
      navigate(`/departments/${deptId}/subdepartments/${link}/articles`);
      toggleMenu();
    }
  };

  return (
    <>
      <MenuItemComponent linkLevel={false} link="#" onClick={handleClick} className="nav__item">
        <ListItemText primary={label} />
        {isExpandable && (
          <FontAwesomeIcon
            icon={open ? faChevronUp : faChevronDown}
            className="icon-chevron"
          />
        )}
      </MenuItemComponent>

      {isExpandable && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {subRoutes.map((sub) => (
              <MenuItem
                key={sub.link}
                label={sub.label}
                link={sub.link}
                toggleMenu={toggleMenu}
                deptId={deptId}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const MobileMenu = ({
  isOpen,
  toggleMenu,
  getToRoot,
  currentActiveParent,
  setCurrentActiveParent,
}) => {
  const [routes, setRoutes] = useState([]);
  const f = useI18nField();
  const t = useL();
  const { lang, setLang } = useLocale();

  useEffect(() => {
    const fetchMenu = async () => {
      const departmentsSnap = await getDocs(collection(db, "departments"));
      const deptData = await Promise.all(
        departmentsSnap.docs.map(async (deptDoc) => {
          const subSnap = await getDocs(
            collection(db, "departments", deptDoc.id, "subdepartments")
          );
          return {
            id: deptDoc.id,
            ...deptDoc.data(),
            subdepartments: subSnap.docs.map((subDoc) => ({
              id: subDoc.id,
              ...subDoc.data(),
            })),
          };
        })
      );
      setRoutes(deptData);
    };
    fetchMenu();
  }, []);

  const toggleParent = (data) => setCurrentActiveParent(data);

  if (!isOpen) return null;

  return (
    <div id="mobileNav" className="is-active flex flex-col justify-between h-full">
      <ul className="nav root-nav flex-1 overflow-y-auto">
        <li className="nav__item">
          <Link className="nav__link" to="/" onClick={toggleMenu}>
            {t({ en: "Home", fr: "Accueil" })}
          </Link>
        </li>

        <li className="nav__item">
          <button
            className="nav__link"
            onClick={() => toggleParent("nav02")}
            type="button"
          >
            {t({ en: "Departments", fr: "Départements" })}{" "}
            <FontAwesomeIcon icon={faChevronRight} />
          </button>

          <div
            className={`nav__sub ${
              currentActiveParent === "nav02" ? "is-active" : ""
            }`}
          >
            <BackLink getToRoot={getToRoot} />
            <div className="collpsible_nav">
              <ul className="collpsible_nav_menu nav flex-column">
                {routes.map((dept) => {
                  const deptLabel = f(dept, "name");
                  const subRoutes = (dept.subdepartments || []).map((sub) => ({
                    label: f(sub, "name"),
                    link: sub.id,
                  }));

                  return (
                    <li key={dept.id}>
                      <MenuItem
                        label={deptLabel}
                        link={dept.id}
                        subRoutes={subRoutes}
                        toggleMenu={toggleMenu}
                        deptId={dept.id}
                      />
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </li>

        <li className="nav__item">
          <Link className="nav__link" to="/specials" onClick={toggleMenu}>
            {t({ en: "Promotions", fr: "Promotions" })}
          </Link>
        </li>
        <li className="nav__item">
          <Link className="nav__link" to="/services" onClick={toggleMenu}>
            {t({ en: "Services", fr: "Services" })}
          </Link>
        </li>
        <li className="nav__item">
          <Link className="nav__link" to="/about-us" onClick={toggleMenu}>
            {t({ en: "About Us", fr: "À propos" })}
          </Link>
        </li>
        <li className="nav__item">
          <Link className="nav__link" to="/blog" onClick={toggleMenu}>
            {t({ en: "News", fr: "Actualités" })}
          </Link>
        </li>
        <li className="nav__item">
          <Link className="nav__link" to="/contact" onClick={toggleMenu}>
            {t({ en: "Contact", fr: "Contact" })}
          </Link>
        </li>
      </ul>

      {/* Language toggle footer */}
      <div className="sticky bottom-0 border-t border-gray-700 bg-white py-3 flex justify-center">
        <LangToggle lang={lang} setLang={setLang} />
      </div>
    </div>
  );
};

export default MobileMenu;
